import React from 'react';
import { MousePointer2, PlayCircle, RotateCcw, Send, X, XCircle } from 'lucide-react';
import './MyReports.css'; // .kanban-guide-card

/**
 * The "how the board works" sheet for the Interactive Dashboard.
 *
 * Shown automatically the first time an authority opens the board (per account,
 * see `kanbanGuideSeenKey`) and on demand from the board header afterwards.
 * Dragging is invisible until you try it, so the sheet leads with a small
 * animation of a card being dragged rather than a paragraph about it.
 */

export const kanbanGuideSeenKey = (userId: number) => `kanbanGuideSeen_${userId}`;

// Storage can be unavailable (private mode, blocked site data). Failing to
// remember just means the guide shows again — never worth breaking the board.
export const hasSeenKanbanGuide = (userId: number): boolean => {
	try {
		return localStorage.getItem(kanbanGuideSeenKey(userId)) === '1';
	} catch {
		return true;
	}
};

export const markKanbanGuideSeen = (userId: number) => {
	try {
		localStorage.setItem(kanbanGuideSeenKey(userId), '1');
	} catch {
		// see above
	}
};

const MOVES: { icon: JSX.Element; iconBg: string; move: string; action: string }[] = [
	{
		icon: <PlayCircle className='h-4 w-4 text-amber-600' />,
		iconBg: 'bg-amber-100',
		move: 'Received → In Progress',
		action: 'Activate: tells the reporter you have started on it',
	},
	{
		icon: <XCircle className='h-4 w-4 text-red-600' />,
		iconBg: 'bg-red-100',
		move: 'In Progress → Closed',
		action: 'Close: only reports in progress can be closed',
	},
	{
		icon: <Send className='h-4 w-4 text-blue-600' />,
		iconBg: 'bg-blue-100',
		move: 'Received / In Progress → Forwarded',
		action: 'Forward: hand the report to another authority',
	},
	{
		icon: <RotateCcw className='h-4 w-4 text-green-600' />,
		iconBg: 'bg-green-100',
		move: 'Closed → In Progress',
		action: 'Reopen: only for reports you closed yourself',
	},
];

const MiniColumn = ({ label, dot }: { label: string; dot: string }) => (
	<div className='flex h-24 flex-col rounded-lg border border-gray-200 bg-white/70 p-1.5'>
		<div className='mb-1.5 flex items-center gap-1 text-[10px] font-bold text-gray-600'>
			<span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
			{label}
		</div>
	</div>
);

const KanbanGuide = ({ onClose }: { onClose: () => void }) => (
	<div
		className='fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm'
		onClick={onClose}
	>
		<div
			role='dialog'
			aria-modal='true'
			aria-labelledby='kanban-guide-title'
			className='relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl'
			onClick={(e) => e.stopPropagation()}
		>
			<button
				type='button'
				onClick={onClose}
				aria-label='Close'
				className='absolute right-3 top-3 z-10 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600'
			>
				<X className='h-4 w-4' />
			</button>

			{/* Animated mini board */}
			<div className='bg-gradient-to-b from-blue-50 to-white px-6 pb-4 pt-8'>
				<div className='relative grid grid-cols-3 gap-2'>
					<MiniColumn label='Received' dot='bg-slate-500' />
					<MiniColumn label='In Progress' dot='bg-amber-500' />
					<MiniColumn label='Closed' dot='bg-emerald-500' />
					{/* Same width as one column (3 columns, two 8px gaps), so the
					    keyframes can step it by exactly one column at a time. */}
					<div
						className='kanban-guide-card pointer-events-none absolute left-0 top-7 px-1.5'
						style={{ width: 'calc((100% - 16px) / 3)' }}
					>
						<div className='relative rounded-md border border-gray-200 bg-white p-1.5 shadow-md'>
							<div className='h-1.5 w-4/5 rounded bg-gray-300' />
							<div className='mt-1 h-1.5 w-1/2 rounded bg-gray-200' />
							<MousePointer2 className='absolute -bottom-3 right-1 h-4 w-4 fill-[#022c56] text-white drop-shadow' />
						</div>
					</div>
				</div>
			</div>

			<div className='px-6 pb-6'>
				<span className='inline-block rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700'>
					New
				</span>
				<h3 id='kanban-guide-title' className='mb-1 mt-2 text-xl font-bold text-gray-900'>
					Drag reports between stages
				</h3>
				<p className='mb-4 text-sm leading-relaxed text-gray-600'>
					Pick up a card and drop it on another column to change its status. Prefer clicking?
					Every card also has buttons for the same moves. You confirm every move before it
					happens, and clicking a card still opens the report.
				</p>

				<ul className='mb-5 space-y-2'>
					{MOVES.map((m) => (
						<li key={m.move} className='flex items-start gap-3'>
							<span
								className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${m.iconBg}`}
							>
								{m.icon}
							</span>
							<div className='min-w-0 text-left'>
								<div className='text-[13px] font-semibold text-gray-900'>{m.move}</div>
								<div className='text-xs text-gray-500'>{m.action}</div>
							</div>
						</li>
					))}
				</ul>

				<button
					type='button'
					onClick={onClose}
					className='w-full rounded-xl bg-[#022c56] px-4 py-2.5 font-semibold text-white transition-colors hover:bg-[#034068]'
				>
					Got it
				</button>
			</div>
		</div>
	</div>
);

export default KanbanGuide;
